/**
 * El cuerpo de la pantalla 404, con el marco de la vitrina (FEATURE-178 RF-07).
 *
 * **Recibe los textos por props a propósito.** Hay dos 404 en el árbol y solo uno tiene
 * i18n: el de `[locale]` lo traduce con `useTranslations`, y el **raíz**
 * (`src/app/not-found.tsx`) vive fuera del árbol de locale —renderiza su propio
 * `<html>`— y no tiene provider de traducciones. Ese raíz es, además, **el que la gente ve
 * de verdad**: una URL inexistente bajo `/es/` cae ahí. Compartir el cuerpo y dejar los
 * textos afuera es lo que permite que los dos se vean igual.
 *
 * **Lo que esto NO cubre.** El 404 más frecuente de este producto llega por WhatsApp —
 * alguien reenvía el link de una liga que ya no está— y ése no pasa por acá: la vista del
 * hincha resuelve la liga en runtime y hoy redirige a `/` cuando no la encuentra. Es un
 * estado de esa superficie, no un 404 de Next.
 */
import Link from "next/link";
import { Container, Puntos } from "@/components/brand/pincel";
import { COLOR } from "@/components/brand/elementos";
import { CORREO_CONTACTO } from "@/components/brand/mock";
import { VitrinaShell } from "./VitrinaShell";

export interface TextosNoEncontrada {
  titulo: string;
  descripcion: string;
  irAlInicio: string;
  escribenos: string;
}

export function NoEncontrada({ textos }: { textos: TextosNoEncontrada }) {
  return (
    <VitrinaShell interior>
      <section className="relative overflow-hidden">
        <Puntos tono="accent" className="absolute -left-10 top-6 h-56 w-72 opacity-[0.10]" />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(700px 340px at 50% -15%, rgb(132 204 22 / 0.10), transparent 62%)" }}
        />

        <Container className="relative py-20 text-center sm:py-28">
          {/* Marca de agua: va DETRÁS del bloque entero, no encima del titular. */}
          <p
            aria-hidden
            className="font-display pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[clamp(140px,26vw,300px)] leading-none text-ink/[0.055]"
          >
            404
          </p>

          <div className="relative">
            <h1 className="v4-sombra font-display text-[clamp(28px,5.2vw,48px)] uppercase leading-[0.96] text-ink">
              {textos.titulo}
            </h1>
            <p className="mx-auto mt-5 max-w-lg text-[15px] leading-relaxed text-muted">{textos.descripcion}</p>

            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link
                href="/es"
                className="font-display inline-flex h-12 items-center rounded-full bg-accent px-6 text-[13px] uppercase tracking-[0.14em] text-onbright transition hover:brightness-110"
              >
                {textos.irAlInicio}
              </Link>
              {/* El canal es el correo: no hay formulario de contacto y no lo va a haber. */}
              <a
                href={`mailto:${CORREO_CONTACTO}`}
                className="font-display inline-flex h-12 items-center rounded-full border-2 px-6 text-[13px] uppercase tracking-[0.14em] transition hover:bg-white/5"
                style={{ borderColor: COLOR.ink, color: COLOR.ink }}
              >
                {textos.escribenos}
              </a>
            </div>
          </div>
        </Container>
      </section>
    </VitrinaShell>
  );
}
